import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import styled from 'styled-components/macro';
import Button from '../../shared/Button';
import { isFetched, PatchType } from '../../../store/appState';
import * as Actions from '../../../store/actions';
import { ResourceStrings } from '../../../ResourceStrings';
import Flex from '../../shared/Flex';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { stringFromErrorOrAny } from '../../../utils/tools';
import { App, SignInState } from '../../../app';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import { Dialog, DialogFooter } from '@fluentui/react/lib/Dialog';
import { User } from '../../../Moderation';
import { operationKey, UIOperationState, useOperation } from '../../../store/operation';
import { formatNumber } from 'humanize-plus';

const pageSize: number = 20;

export default function UsersList(): JSX.Element {
    const searchUsersResult = useSelector((state: RootState) => state.appState.usersListForAdminView);
    const [pageNumber, setPageNumber] = useState(0);
    const previousPageNumber = useRef<number>();    // To ensure scrollIntoView below isn't called on first page load
    const signinState = useSelector((state: RootState) => state.appState.signInState);
    const [searchText, setSearchText] = useState<string>('');
    const [forceSearchAgain, setForceSearchAgain] = useState(true);  // Needed to force search if user doesn't change any filters or search-text but hits 'Search' button

    const usersUpdatedSuspensionState = useSelector((state: RootState) => state.appState.usersUpdatedSuspensionState);
    const [suspensionActionInfo, setSuspensionActionInfo] = useState<User>(null);
    const showSuspensionDialog = suspensionActionInfo != null;

    useEffect(() => {
        if (signinState === SignInState.Checking || signinState === SignInState.Executing) {
            return;
        }

        Actions.searchUsersForAdminView(searchText, pageNumber, pageSize);
    }, [pageNumber, pageSize, signinState, forceSearchAgain]);

    useEffect(() => {
        // Scroll to top when page is changed
        if (previousPageNumber.current != null) {
            document.getElementById('admin-users-list')?.scrollIntoView();
        }
    }, [pageNumber]);

    const suspensionOpKey = operationKey(suspensionActionInfo?.principalId, PatchType.UpdateUserSuspensionState);
    const suspensionOperation = useOperation(suspensionOpKey);

    const handlePageChange = (newPageNumber: number): void => {
        previousPageNumber.current = pageNumber;
        setPageNumber(newPageNumber);
    }

    const onSearchRequested = (): void => {
        setPageNumber(0);
        setForceSearchAgain(x => !x);
    };

    const onSearchBoxKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === "Enter") {
            onSearchRequested();
        }
    }

    const onSubmitSuspensionDialog = (): void => {
        suspensionOperation.resetOperation();
        Actions.updateUserSuspensionState(suspensionActionInfo.principalId, isUserSuspended(suspensionActionInfo) ? Actions.SuspensionActionType.Unsuspend : Actions.SuspensionActionType.Suspend, suspensionOpKey);
    }

    const onCancelSuspensionDialog = (): void => {
        suspensionOperation.resetOperation();
        setSuspensionActionInfo(null);
    }

    const onShowSuspensionConfirmationDialog = (user: User): void => {
        suspensionOperation.resetOperation();
        setSuspensionActionInfo(user);
    }

    const isUserSuspended = (user: User): boolean => {
        // eslint-disable-next-line security/detect-object-injection
        const userUpdatedSuspensionState = user && usersUpdatedSuspensionState[user.principalId];
        return userUpdatedSuspensionState == null || userUpdatedSuspensionState === undefined ? user?.isSuspended : userUpdatedSuspensionState;
    }

    const searchUsers = searchUsersResult?.data?.items ?? [];
    const totalItemsCount = searchUsersResult?.data?.totalItemsCount ?? 0;
    const maxPages = totalItemsCount / pageSize;

    return (
        <Wrapper id="admin-users-list">
            <Dialog
                hidden={!showSuspensionDialog}
                onDismiss={() => onCancelSuspensionDialog()}
                dialogContentProps={{
                    title:
                        suspensionOperation.operation?.state === UIOperationState.Completed
                            ? ''
                            : `${isUserSuspended(suspensionActionInfo) ? 'Unsuspend' : 'Suspend'} user`,
                    subText:
                        suspensionOperation.operation?.state === UIOperationState.Completed
                            ? ''
                            : `Do you want to ${isUserSuspended(suspensionActionInfo) ? 'unsuspend' : 'suspend'} user '${suspensionActionInfo?.displayName}'' having email id '${suspensionActionInfo?.email}'?`,
                    closeButtonAriaLabel: 'Close',
                }}
            >
                {suspensionOperation.isInProgress() && <div>Updating user&aps;s suspension state...</div>}
                {suspensionOperation.operation?.state === UIOperationState.Completed && <div className="info">{suspensionOperation.operation.message ?? "user's suspension state successfully updated"}</div>}
                {suspensionOperation.operation?.state === UIOperationState.Failed && <div className="error" css='margin-left: 1rem'>{suspensionOperation.operation.message ?? "Failure while updating suspension state"}</div>}

                <DialogFooter>
                    {suspensionOperation.operation?.state !== UIOperationState.Completed && <Button onClick={() => onSubmitSuspensionDialog()}>Yes</Button>}
                    {suspensionOperation.operation?.state !== UIOperationState.Completed && <Button onClick={() => onCancelSuspensionDialog()}>Cancel</Button>}
                    {suspensionOperation.operation?.state === UIOperationState.Completed && <Button onClick={() => onCancelSuspensionDialog()}>Close</Button>}
                </DialogFooter>
            </Dialog>

            <SearchInputContainer className="top-level-text">
                <SearchText
                    autoFocus
                    value={searchText}
                    onChange={(e, newValue) => setSearchText(newValue)}
                    placeholder="Search users by name, email or VsId"
                    iconProps={{ iconName: 'Search' }}
                    onKeyPress={(e) => onSearchBoxKeyPress(e)}
                />

                <Button onClick={() => onSearchRequested()}>Search</Button>
            </SearchInputContainer>

            <div css="margin-bottom: 1rem">

                {!searchUsersResult.isFetching && <div><b>{searchUsersResult.data?.totalItemsCount ?? 0}</b> matching users found</div>}
                {isFetched(searchUsersResult) && searchUsers.length === 0 && <div>{ResourceStrings.noItemsFound()}</div>}
                {searchUsersResult.isFetching && <Flex justifyContent="center" className="fetching">
                    <Spinner size={SpinnerSize.small} />
                    <span>&nbsp;&nbsp;Fetching results...</span>
                </Flex>}
                {!searchUsersResult.isFetching && searchUsersResult.hasErrors && <div className="error">
                    There was an error in retrieving the results.
                    {searchUsersResult.errors && <pre>{stringFromErrorOrAny(searchUsersResult.errors)}</pre>}
                </div>}
            </div>

            <UsersTable>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>VsId</th>
                        <th>Email</th>
                        <th>Created</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {/* {searchUsers.map(x => <UserRow key={x.principalId + x.vsid} user={x} />)} */}
                    {searchUsers.map(user => (
                        <tr key={user.principalId + user.vsid}>
                            <td>{user.displayName}</td>
                            <td><a href={App.GetUserProfileLink(user.principalId)} target="_blank" rel="noopener noreferrer">{user.vsid}</a></td>
                            <td>{user.email}</td>
                            <td>{App.ParseDateTimeToLocal(user.createdDate)}</td>
                            <td css='text-align: center' className='actions-cell'>
                                <Button buttonType="link" size="compact" onClick={() => onShowSuspensionConfirmationDialog(user)}>{isUserSuspended(user) ? 'Unsuspend' : 'Suspend'}</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </UsersTable>

            <Pager style={{ display: isFetched(searchUsersResult) && totalItemsCount > pageSize ? "flex" : "none", alignItems: 'baseline' }}>
                <Button
                    buttonType="link"
                    disabled={pageNumber <= 0}
                    onClick={() => handlePageChange(pageNumber - 1)}
                >
                    &lt; Previous page
                </Button>
                <span css="font-size: 1.4rem">Page {formatNumber(pageNumber + 1)} of {formatNumber(Math.ceil(totalItemsCount / pageSize))}</span>
                <Button
                    buttonType="link"
                    disabled={pageNumber >= maxPages - 1}
                    onClick={() => handlePageChange(pageNumber + 1)}
                >
                    Next page &gt;
                </Button>
            </Pager>
        </Wrapper >
    );
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 1rem;
`;

const UsersTable = styled.table`
    margin: 0;
    width: 100%;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    display: table;
    border-spacing: 0;

    th {
        padding: .4rem 1rem;
        background-color: var(--color-primary);
        color: white;
        border-bottom: solid 1px var(--color-on-surface-light);
    }

    td {
        font-size: 1.4rem;
        min-width: 10rem;
        padding: .6rem 1rem;
    }

    td.actions-cell {
        min-width: 0;

        button {
            font-weight: normal;
            font-size: 1.2rem;
        }
    }

    th + th {
        border-left: solid 1px white;
    }

    td + td {
        border-left: solid 1px var(--color-on-surface-light);
    }

    >tbody >tr:nth-child(even) {
        background-color: #EAEAEA;
    }
`;

const Pager = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: 1rem;

    button {
        padding: 0 1.6rem;
        font-size: 1.6rem;
    }
`;

const SearchInputContainer = styled.div`
    display: flex;
    margin-bottom: 2rem;

    button {
        padding: 0 1.6rem;
        font-size: 1.6rem;
    }
`;

const SearchText = styled(SearchBox)`
    height: auto;
    border: solid 1px var(--color-surface-dark2);
    margin-right: 1.8rem;
    flex-grow: 1;

    .ms-SearchBox-field {
        font-weight: normal;
        font-size: 1.4rem;
    }

    .ms-SearchBox-field::placeholder {
        font-weight: 350;
        font-size: 1.4rem;
    }

    .ms-SearchBox-icon {
        color: var(--color-on-surface);
    }

    input {
        padding: 0 .6rem;
        border: none;
        font-size: 1.4rem;
    }
`;
